import PageSeo from '../seo/PageSeo';
import { useInitialData } from '../seo/InitialData';
import React, { useMemo, useContext } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import ReactMarkdown from 'react-markdown';
import { AppContext } from "../App";
import { useArchive } from '../api/archive';

import CollectionItemParametry from '../components/CollectionItemParametry'
import CollectionItemPhotos from '../components/CollectionItemPhotos'
import CollectionItemLinks from '../components/CollectionItemLinks'
import ShareButton from '../components/ShareButton';
import HardwareReviewForm from '../components/HardwareReviewForm';
import { releaseFeatures } from '../config/releaseFeatures';
import {
  getCollectionDescription,
  getCollectionHeroImageUrl,
  getCollectionReturnPath,
  getCollectionSpecRows,
  getCollectionSources,
  hasCollectionPhotos,
} from './collectionItemUtils';

export default function CollectionItem() {
  const initial = useInitialData();
  const strapiURL = useContext(AppContext)
  const { id } = useParams();
  const location = useLocation();
  const returnPath = getCollectionReturnPath(location);
  const { archive, error: archiveError } = useArchive();
  const item = initial.item?.documentId === id ? initial.item : archive?.collections.find((entry) => entry.documentId === id) || null;
  const linkedEntries = archive?.links.filter((entry) => entry.manufacturers?.some((manufacturer) => manufacturer.id === item?.manufacturer?.id)) || initial.links || [];
  const itemLinks = linkedEntries.length ? { data: linkedEntries } : null;
  const isLoading = !item && !archive && !archiveError;
  const hasError = Boolean(archiveError && !item);

  const specRows = useMemo(() => getCollectionSpecRows(item || {}), [item]);
  const heroImageUrl = getCollectionHeroImageUrl(item || {}, strapiURL);
  const hasPhotos = hasCollectionPhotos(item || {});
  const hasDescription = Boolean(item?.summary || item?.Opis);
  const hasLinks = itemLinks?.data?.length > 0;
  const sources = useMemo(() => getCollectionSources(item || {}), [item]);
  const relatedBlogs = Array.isArray(item?.relatedBlogs) ? item.relatedBlogs.filter((blog) => blog?.documentId) : [];
  const platforms = Array.isArray(item?.platforms) ? item.platforms.filter((platform) => platform?.slug) : [];
  const benchmarkResults = initial.item?.documentId === id ? initial.benchmarkResults || [] : archive?.['benchmark-results']?.filter((result) => result.collection?.id === item?.id) || [];
  const hardwareReviews = releaseFeatures.reviews
    ? (initial.item?.documentId === id ? initial.hardwareReviews || [] : archive?.['hardware-reviews']?.filter((review) => review.hardwareModel?.documentId === item?.hardwareModel?.documentId && review.moderationStatus === 'approved') || [])
    : [];
  const externalReviews = releaseFeatures.reviews
    ? (initial.item?.documentId === id ? initial.externalReviews || [] : archive?.['external-reviews']?.filter((review) => review.hardwareModel?.id === item?.hardwareModel?.id) || [])
    : [];
  const ratingCount = Number(item?.hardwareModel?.ratingCount ?? hardwareReviews.length);
  const ratingAverage = Number(item?.hardwareModel?.ratingAverage ?? 0);
  const averageRating = ratingCount ? ratingAverage.toFixed(1) : null;

  if (isLoading) {
    return (
      <section className="retro-item-page">
      <PageSeo item={item} unavailable={hasError || isLoading} />
        <div className="retro-item-inner">
          <div className="retro-item-empty">Ładowanie eksponatu...</div>
        </div>
      </section>
    );
  }

  if (hasError || !item) {
    return (
      <section className="retro-item-page">
      <PageSeo item={item} unavailable={hasError || isLoading} />
        <div className="retro-item-inner">
          <Link to={returnPath} className="retro-back-link">
            <i className="feather-chevron-left" aria-hidden="true"></i>
            Wróć do zbiorów
          </Link>
          <div className="retro-item-empty">Nie udało się pobrać eksponatu.</div>
        </div>
      </section>
    );
  }

  return (
    <section className="retro-item-page">
      <PageSeo item={item} unavailable={hasError || isLoading} />
      <div className="retro-item-inner">
        <Link to={returnPath} className="retro-back-link">
          <i className="feather-chevron-left" aria-hidden="true"></i>
          Wróć do zbiorów
        </Link>

        <div className="retro-item-hero">
          <div className="retro-item-copy">
            <div className="retro-item-labels">
              <span>{item.kind?.Name || 'Eksponat'}</span>
              <span>{item.manufacturer?.Name || 'Nieznany producent'}</span>
              {item.RokProdukcji && <span>{item.RokProdukcji}</span>}
            </div>
            <h1>{item.Name}</h1>
            <p>{hasDescription ? getCollectionDescription(item).slice(0, 260) : 'Fotografie i dane techniczne eksponatu z kolekcji StareKomputery.pl.'}</p>

            <div className="retro-item-actions">
              {hasPhotos && <a className="retro-button retro-button-primary" href="#Photos">Zdjęcia</a>}
              {specRows.length > 0 && <a className="retro-button retro-button-secondary" href="#Specyfikacja">Specyfikacja</a>}
              {sources.length > 0 && <a className="retro-button retro-button-secondary" href="#Sources">Źródła</a>}
              {hasLinks && <a className="retro-button retro-button-secondary" href="#Links">Linki</a>}
            </div>
          </div>

          <div className="retro-item-media">
            {heroImageUrl ? (
              <img src={heroImageUrl} alt={item.Name} />
            ) : (
              <div className="retro-item-media-fallback">Brak zdjęcia</div>
            )}
          </div>
        </div>

        <div className="retro-item-layout">
          <aside className="retro-item-sidebar">
            <div className="retro-item-sidebar-card">
              <h2>Eksponat</h2>
              <dl>
                <div>
                  <dt>Producent</dt>
                  <dd>{item.manufacturer?.Name || '-'}</dd>
                </div>
                <div>
                  <dt>Rodzaj</dt>
                  <dd>{item.kind?.Name || '-'}</dd>
                </div>
                <div>
                  <dt>Zdjęcia</dt>
                  <dd>{item.Photos?.length || 0}</dd>
                </div>
              </dl>
              <ShareButton className="retro-share-button" />
            </div>

            <nav className="retro-item-sidebar-card retro-item-nav" aria-label="Sekcje eksponatu">
              {hasPhotos && <a href="#Photos">Zdjęcia</a>}
              {specRows.length > 0 && <a href="#Specyfikacja">Specyfikacja</a>}
              {hasDescription && <a href="#Opis">Opis</a>}
              {item.Historia && <a href="#Historia">Historia</a>}
              {item.condition && <a href="#Stan">Stan egzemplarza</a>}
              {relatedBlogs.length > 0 && <a href="#Related">Powiązane wpisy</a>}
              {sources.length > 0 && <a href="#Sources">Źródła</a>}
              {hasLinks && <a href="#Links">Adresy stron</a>}
            </nav>
          </aside>

          <div className="retro-item-content">
            {(hasDescription || item.Historia) && (
              <section className="retro-item-section retro-reading-section">
                {item.summary && <div id="Opis"><h2>W skrócie</h2><p>{item.summary}</p></div>}
                {item.Opis && <div id={item.summary ? undefined : 'Opis'}><h2>{item.summary ? 'Opis eksponatu' : 'Opis eksponatu'}</h2><ReactMarkdown>{item.Opis}</ReactMarkdown></div>}
                {item.Historia && <div id="Historia"><h2>Historia</h2><ReactMarkdown>{item.Historia}</ReactMarkdown></div>}
              </section>
            )}
            {item.condition && <section className="retro-item-section retro-reading-section" id="Stan"><h2>Stan egzemplarza</h2><ReactMarkdown>{item.condition}</ReactMarkdown></section>}
            {hasPhotos && (
              <section className="retro-item-section" id="Photos">
                <div className="retro-item-section-header">
                  <h2>Zdjęcia</h2>
                  <p>{item.Photos.length} plików</p>
                </div>
                <CollectionItemPhotos d={item} />
              </section>
            )}

            <section className="retro-item-section" id="Specyfikacja">
              <div className="retro-item-section-header">
                <h2>Specyfikacja</h2>
                <p>{specRows.length} pól</p>
              </div>
              <CollectionItemParametry d={item} />
            </section>

            {platforms.length > 0 && <section className="retro-item-section" id="Games"><div className="retro-item-section-header"><h2>Gry na tej platformie</h2><p>{platforms.length}</p></div><p>Zobacz wydania przypisane do platformy zgodności. Wyniki działania na tym egzemplarzu są publikowane osobno.</p><ul className="retro-link-list">{platforms.map((platform) => <li className="retro-link-row" key={platform.slug}><Link to={`/platform/${platform.slug}`}>{platform.Name}</Link></li>)}</ul></section>}

            {benchmarkResults.length > 0 && <section className="retro-item-section" id="Benchmarks"><div className="retro-item-section-header"><h2>Testy działania tego egzemplarza</h2><p>{benchmarkResults.length}</p></div><ul className="retro-link-list">{benchmarkResults.map((result) => <li className="retro-link-row" key={result.documentId}><Link to={`/games/${result.gameRelease?.documentId}`}>{result.gameRelease?.Name || 'Wydanie gry'}</Link><span>{result.compatibility === 'works' ? 'działa' : result.compatibility === 'partial' ? 'działa częściowo' : 'nie działa'}{result.metricName ? ` · ${result.metricName}: ${result.metricValue} ${result.metricUnit}` : ''}</span></li>)}</ul></section>}

            {releaseFeatures.reviews && item?.hardwareModel && <section className="retro-item-section" id="Reviews"><div className="retro-item-section-header"><h2>Opinie o modelu</h2><p>{averageRating ? `${averageRating}/5 · ${ratingCount} ${ratingCount === 1 ? 'opinia' : 'opinii'}` : 'Brak ocen'}</p></div><div className="retro-section-copy"><p>Oceny dotyczą modelu {item.hardwareModel.Name}, a nie stanu tego egzemplarza.</p></div>{hardwareReviews.length > 0 && <ul className="retro-link-list">{hardwareReviews.map((review) => <li className="retro-link-row" key={review.documentId}><strong>{review.rating}/5</strong><p>{review.body}</p></li>)}</ul>}<HardwareReviewForm hardwareModelDocumentId={item.hardwareModel.documentId} modelName={item.hardwareModel.Name} /></section>}

            {releaseFeatures.reviews && externalReviews.length > 0 && <section className="retro-item-section" id="ExternalReviews"><div className="retro-item-section-header"><h2>Oceny zewnętrzne</h2><p>{externalReviews.length}</p></div><p>Nie są częścią średniej społeczności.</p><ul className="retro-link-list">{externalReviews.map((review) => <li className="retro-link-row" key={review.documentId}><a href={review.url} target="_blank" rel="noopener noreferrer">{review.publisher}</a><span>{review.value ?? '—'} / {review.scale} · dostęp: {review.accessedAt}</span>{review.usageNote && <p>{review.usageNote}</p>}</li>)}</ul></section>}

            {relatedBlogs.length > 0 && <section className="retro-item-section" id="Related"><div className="retro-item-section-header"><h2>Powiązane wpisy</h2><p>{relatedBlogs.length}</p></div><ul className="retro-link-list">{relatedBlogs.map((blog) => <li className="retro-link-row" key={blog.documentId}><Link to={`/blog/${blog.documentId}`}>{blog.Title || 'Wpis w archiwum'}</Link>{blog.Date && <span>{blog.Date}</span>}</li>)}</ul></section>}

            {sources.length > 0 && <section className="retro-item-section" id="Sources"><div className="retro-item-section-header"><h2>Źródła</h2><p>{sources.length}</p></div><ul className="retro-link-list">{sources.map((source) => <li className="retro-link-row" key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label}</a>{source.note && <span>{source.note}</span>}</li>)}</ul></section>}

            {hasLinks && (
              <section className="retro-item-section" id="Links">
                <div className="retro-item-section-header">
                  <h2>Adresy stron</h2>
                  <p>Źródła: {itemLinks.data.length}</p>
                </div>
                <CollectionItemLinks linksArray={itemLinks} />
              </section>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
